import { TablePagination } from '@components/supply-chain/TablePagination'
import { Button } from '@shadcn/components/ui/button'
import { Card, CardContent as CardBody, CardHeader, CardTitle } from '@shadcn/components/ui/card'
import { Col } from '@shadcn/components/ui/col'
import { Input } from '@shadcn/components/ui/input'
import { Label } from '@shadcn/components/ui/label'
import { Row } from '@shadcn/components/ui/row'
import { Activity, ArrowDown, ArrowUp, Calendar, ChevronLeft, MapPin, Package, TrendingDown, TrendingUp, User } from 'lucide-react'
import * as moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import useQuery from '../../helpers/JIFI/useQuery'
import { stockMovementSourceLabel } from './constants'
import { getMovementDisplayDeltas } from './movement-history.utils'

const SkuHistoryPage = () => {
  const history = useHistory()
  const location = useLocation()
  const { sku } = useParams()
  const queryParams = new URLSearchParams(location.search)
  const locationCode = queryParams.get('locationCode')
  const legacyLocationName = locationCode ? undefined : queryParams.get('location')
  const locationLabel = locationCode || legacyLocationName

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')

  const [data, state] = useQuery({
    url: `/inventory/movement-history/sku`,
    refresh: true,
    queryData: {
      sku,
      locationCode: locationCode || undefined,
      locationName: legacyLocationName,
      page,
      perPage,
      fromDate: startDate || undefined,
      toDate: endDate || undefined,
      sortOrder,
    }
  })

  const historyData = data?.data || []
  const count = data?.count || 0
  const currentReserved = data?.currentBalance?.reserved

  useEffect(() => {
    document.title = `SKU History: ${sku} | JIFI`
  }, [sku])

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value)
    setPage(1)
  }

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value)
    setPage(1)
  }

  const toggleSortOrder = () => {
    setSortOrder(current => current === 'desc' ? 'asc' : 'desc')
    setPage(1)
  }

  const handleBack = () => {
    history.push('/movement-history')
  }

  return (
    <div className="min-h-screen bg-gray-50/50 page-content">
      <div className="container mx-auto px-4 max-w-7xl">

        {/* Header Section */}
        <div className="mb-4">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Stock Movements
          </Button>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SKU History</h1>
                <p className="text-gray-600">Track inventory movements and changes</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">SKU:</span>
                <span className="px-3 py-1 bg-gray-100 rounded-md font-mono text-sm">{sku}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <span className="font-medium text-gray-700">Location:</span>
                <span className="px-3 py-1 bg-gray-100 rounded-md">{locationLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Current Reserved:</span>
                <span className="px-3 py-1 bg-gray-100 rounded-md font-mono text-sm">
                  {Number.isFinite(currentReserved) ? currentReserved : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Filter Period
            </CardTitle>
          </CardHeader>
          <CardBody>
            <Row className="gap-4">
              <Col className="w-full sm:w-1/2 lg:w-1/4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-medium text-gray-700">
                    From Date
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className="w-full"
                  />
                </div>
              </Col>
              <Col className="w-full sm:w-1/2 lg:w-1/4">
                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-medium text-gray-700">
                    To Date
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className="w-full"
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Data Table Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Movement History
              {count > 0 && (
                <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {count} entries
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="w-full overflow-hidden">
              {state === 'success' ? (
                historyData.length > 0 ? (
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '36%' }} />
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '17%' }} />
                      <col style={{ width: '11%' }} />
                    </colgroup>
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 font-semibold text-gray-700">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                            onClick={toggleSortOrder}
                            title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
                          >
                            Date & Time
                            {sortOrder === 'desc'
                              ? <ArrowDown className="w-4 h-4" />
                              : <ArrowUp className="w-4 h-4" />}
                          </button>
                        </th>
                        <th className="text-left py-3 px-2 font-semibold text-gray-700 whitespace-nowrap">Activity</th>
                        <th className="text-left py-3 px-2 font-semibold text-gray-700 whitespace-nowrap">User</th>
                        <th className="text-left py-3 px-2 font-semibold text-gray-700 whitespace-nowrap">Source</th>
                        <th className="text-left py-3 px-2 font-semibold text-gray-700 whitespace-nowrap">Changes</th>
                        <th className="text-right py-3 px-2 font-semibold text-gray-700 whitespace-nowrap">On Hand (After)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map((item, index) => {
                        const movementDeltas = getMovementDisplayDeltas(item)
                        const onHandDelta = movementDeltas.find(delta => delta.field === 'onHand')
                        const onHandAfter = item.quantityAfter ?? 0
                        const onHandBefore = item.quantityBefore ?? 0

                        return (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-2 align-middle">
                              <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
                                <Calendar className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                                <div className="min-w-0 leading-tight">
                                  <span className="block text-sm whitespace-nowrap">
                                    {moment.tz(item.createdAt, "Australia/Melbourne").format('D MMM YYYY')}
                                  </span>
                                  <span className="block mt-1 text-xs text-gray-500 whitespace-nowrap">
                                    {moment.tz(item.createdAt, "Australia/Melbourne").format('h:mm a z')}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-2 align-middle whitespace-normal break-words">
                              <span className="text-sm leading-snug text-gray-900">{item.message || '-'}</span>
                            </td>
                            <td className="py-3 px-2 align-middle overflow-hidden whitespace-nowrap">
                              <div className="flex min-w-0 items-center gap-2">
                                <User className="w-4 h-4 shrink-0 text-gray-400" />
                                <span
                                  className="min-w-0 truncate text-sm text-gray-700"
                                  title={item.userName || '-'}
                                >
                                  {item.userName || '-'}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-2 align-middle whitespace-nowrap">
                              <span className="text-sm text-gray-700">
                                {item.source ? stockMovementSourceLabel[item.source] || item.source : ''}
                              </span>
                            </td>
                            <td className="py-3 px-2 align-middle">
                              <div className="flex flex-wrap items-center gap-1">
                                {movementDeltas.length > 0 ? movementDeltas.map(delta => {
                                  const isPositive = delta.quantity > 0
                                  return (
                                    <div
                                      key={delta.field}
                                      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium ${
                                        isPositive
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-red-100 text-red-800'
                                      }`}
                                    >
                                      {isPositive
                                        ? <TrendingUp className="w-3 h-3" />
                                        : <TrendingDown className="w-3 h-3" />}
                                      {delta.label} {isPositive ? '+' : ''}{delta.quantity}
                                    </div>
                                  )
                                }) : <span className="text-sm text-gray-400">No quantity change</span>}
                              </div>
                            </td>
                            <td className="py-3 px-2 align-middle text-right whitespace-nowrap">
                              <div className="inline-flex items-center justify-end gap-2">
                                <span className="text-sm font-medium text-gray-900">{onHandAfter}</span>
                                {onHandDelta ? (
                                  <span className="text-xs text-gray-500" title={`Before: ${onHandBefore}`}>
                                    (was {onHandBefore})
                                  </span>
                                ) : <span className="text-xs text-gray-500">(unchanged)</span>}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No movement history found for this period.</p>
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  {Array.from({ length: perPage }).map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {state === 'success' && historyData.length > 0 && (
              <div className="mt-6">
                <TablePagination
                  count={count}
                  page={page}
                  setPage={setPage}
                  perPage={perPage}
                  setPerPage={setPerPage}
                />
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default SkuHistoryPage
